/**
 * GameActions - Command layer for UI interactions
 * 
 * Benefits:
 * - Decouples UI from direct GameState mutations
 * - Centralizes action logic for easier testing
 * - Provides clear API for what users can do
 * - Future: Enable undo/redo, action logging, replay
 * 
 * Pattern: Each action is a method that encapsulates a game operation
 */

import type { GameState } from '../GameState';

export class GameActions {
  constructor(private gameState: GameState) {}

  // ============================================
  // PRODUCTION ACTIONS
  // ============================================

  /**
   * Make a single paperclip (manual click)
   */
  makeClip(): void {
    this.gameState.clipClick();
  }

  /**
   * Buy an AutoClipper
   * @returns true if purchase succeeded
   */
  buyAutoClipper(): boolean {
    const cost = this.gameState.clipperCost;
    if (this.gameState.funds >= cost) {
      this.gameState.buyClipper();
      return true;
    }
    return false;
  }

  /**
   * Buy a MegaClipper
   * @returns true if purchase succeeded
   */
  buyMegaClipper(): boolean {
    const cost = this.gameState.megaClipperCost;
    if (this.gameState.funds >= cost) {
      this.gameState.buyMegaClipper();
      return true;
    }
    return false;
  }

  // ============================================
  // BUSINESS ACTIONS
  // ============================================

  /**
   * Lower the clip price
   */
  lowerPrice(): void {
    this.gameState.lowerPrice();
  }

  /**
   * Raise the clip price
   */
  raisePrice(): void {
    this.gameState.raisePrice();
  }

  /**
   * Buy a marketing level
   * @returns true if purchase succeeded
   */
  buyMarketing(): boolean {
    const cost = this.gameState.marketingCost;
    if (this.gameState.funds >= cost) {
      this.gameState.buyMarketing();
      return true;
    }
    return false;
  }

  /**
   * Buy wire
   * @returns true if purchase succeeded
   */
  buyWire(): boolean {
    const cost = this.gameState.wireCost;
    if (this.gameState.funds >= cost) {
      this.gameState.buyWire();
      return true;
    }
    return false;
  }

  /**
   * Toggle auto-wire buyer
   */
  toggleAutoWire(): void {
    this.gameState.wireBuyerFlag = this.gameState.wireBuyerFlag ? 0 : 1;
  }

  // ============================================
  // COMPUTATIONAL ACTIONS
  // ============================================

  /**
   * Buy a processor
   * @returns true if purchase succeeded
   */
  buyProcessor(): boolean {
    const cost = this.gameState.procCost;
    if (this.gameState.trust >= cost) {
      this.gameState.trust -= cost;
      this.gameState.processors++;
      this.gameState.procCost = Math.pow(1.5, this.gameState.processors);
      return true;
    }
    return false;
  }

  /**
   * Buy memory
   * @returns true if purchase succeeded
   */
  buyMemory(): boolean {
    const cost = this.gameState.memCost;
    if (this.gameState.trust >= cost) {
      this.gameState.trust -= cost;
      this.gameState.memory++;
      this.gameState.memoryLevel = this.gameState.memory;
      this.gameState.maxOps = this.gameState.memory * 1000;
      this.gameState.memCost = Math.pow(1.5, this.gameState.memory);
      return true;
    }
    return false;
  }

  /**
   * Set creativity generation state
   */
  setCreativity(enabled: boolean): void {
    this.gameState.creativityOn = enabled ? 1 : 0;
  }

  // ============================================
  // FACTORY ACTIONS
  // ============================================

  /**
   * Buy a factory
   * @returns true if purchase succeeded
   */
  buyFactory(): boolean {
    return this.gameState.buyFactory();
  }

  /**
   * Buy a harvester
   * @returns true if purchase succeeded
   */
  buyHarvester(): boolean {
    return this.gameState.buyHarvester();
  }

  /**
   * Buy a wire drone
   * @returns true if purchase succeeded
   */
  buyWireDrone(): boolean {
    return this.gameState.buyWireDrone();
  }

  // ============================================
  // INVESTMENT ACTIONS
  // ============================================

  /**
   * Deposit funds into investment pool
   */
  depositInvestment(amount: number): boolean {
    if (this.gameState.funds >= amount) {
      this.gameState.funds -= amount;
      this.gameState.bankroll += amount;
      return true;
    }
    return false;
  }

  /**
   * Withdraw funds from investment pool
   */
  withdrawInvestment(amount: number): boolean {
    if (this.gameState.bankroll >= amount) {
      this.gameState.bankroll -= amount;
      this.gameState.funds += amount;
      return true;
    }
    return false;
  }

  /**
   * Change investment risk level
   */
  setRiskLevel(level: number): void {
    this.gameState.riskLevel = level;
  }

  /**
   * Upgrade investment engine
   */
  upgradeInvestmentEngine(): boolean {
    const cost = this.gameState.investUpgradeCost;
    if (this.gameState.funds >= cost) {
      this.gameState.funds -= cost;
      this.gameState.investLevel++;
      this.gameState.investUpgradeCost = 500 * Math.pow(2, this.gameState.investLevel);
      return true;
    }
    return false;
  }

  // ============================================
  // TOURNAMENT ACTIONS
  // ============================================

  /**
   * Select tournament strategy
   */
  selectStrategy(strategyIndex: number): void {
    this.gameState.stratPicker = strategyIndex;
  }

  // ============================================
  // SPACE ACTIONS
  // ============================================

  /**
   * Adjust probe stats (combat vs replication)
   * These are managed directly through GameState properties
   * More complex space actions can be added as needed
   */
  adjustProbeStats(stat: 'combat' | 'replication', value: number): void {
    if (stat === 'combat') {
      this.gameState.probeHaz = value;
    } else {
      this.gameState.probeRep = value;
    }
  }

  // ============================================
  // PROJECT ACTIONS
  // ============================================

  /**
   * Purchase a project by ID
   */
  purchaseProject(projectId: number): boolean {
    // This requires importing purchaseProject from projects.ts
    // For now, delegate to the existing system
    const { purchaseProject } = require('../../data/projects');
    return purchaseProject(this.gameState, projectId);
  }

  // ============================================
  // SAVE/LOAD ACTIONS
  // ============================================

  /**
   * Save game to slot
   */
  saveGame(slot: number = 0): void {
    this.gameState.save(slot);
  }

  /**
   * Load game from slot
   */
  loadGame(slot: number = 0): boolean {
    return this.gameState.load(slot);
  }

  /**
   * Auto-save game
   */
  autoSave(): void {
    this.gameState.autoSave();
  }
}
